const apiEnvMapping = {
  www: '',
  demo: '-demo',
  staging: '-staging',
  dev: '-dev',
  pay: '',
  'pay-demo': '-demo',
  'pay-staging': '-staging',
  'pay-dev': '-dev',
};

const pageEnvMapping = {
  www: 'prod',
  demo: 'demo',
  staging: 'staging',
  dev: 'dev',
  pay: 'prod',
  'pay-demo': 'demo',
  'pay-staging': 'staging',
  'pay-dev': 'dev',
};

const internalCorsHosts = {
  staging: 'checkout.stg.lumi.pub',
  demo: 'checkout.demo.lumi.pub',
};

const previewHosts = new Set(['pay.sandbox.airwallex.com', 'shadow-pay-demo.airwallex.com']);

// `www.sandbox.airwallex.com` must not be treated as prod `www`: the first
// label is `www`, but the APIs live under the `.sandbox` suffix. Match the
// whole `*.sandbox.airwallex.com` zone plus the non-pattern shadow host.
const isSandboxHost = (hostName: string): boolean =>
  previewHosts.has(hostName) || hostName === 'sandbox.airwallex.com' || hostName.endsWith('.sandbox.airwallex.com');

export const getApiEnvByHost = (): string => {
  const env = window.location.host.split('.')[0] as keyof typeof apiEnvMapping;
  const hostName = window.location.hostname;

  // For internally testing cross domain checkout
  if (hostName === internalCorsHosts.staging) {
    return '-staging';
  } else if (hostName === internalCorsHosts.demo) {
    return '-demo';
  } else if (isSandboxHost(hostName)) {
    return '.sandbox';
  }

  if (env.includes('localhost')) {
    return apiEnvMapping.staging;
  }
  return typeof apiEnvMapping[env] !== 'undefined' ? apiEnvMapping[env] : '';
};

export const getPageEnvByHost = (): string => {
  const env = window.location.host.split('.')[0] as keyof typeof apiEnvMapping;
  const hostName = window.location.hostname;

  // For internally testing cross domain checkout
  if (hostName === internalCorsHosts.staging) {
    return 'staging';
  } else if (hostName === internalCorsHosts.demo) {
    return 'demo';
  } else if (isSandboxHost(hostName)) {
    return 'preview';
  }

  return pageEnvMapping[env] || 'prod';
};

export const getComponentsSDKEnvByHost = (): string => {
  const env = window.location.host.split('.')[0] as keyof typeof apiEnvMapping;
  const hostName = window.location.hostname;

  // For internally testing cross domain checkout
  if (hostName === internalCorsHosts.staging) {
    return 'staging';
  } else if (hostName === internalCorsHosts.demo) {
    return 'demo';
  } else if (isSandboxHost(hostName)) {
    return 'sandbox';
  }

  return pageEnvMapping[env] || 'prod';
};

// Legacy checkout hosts whose payment-link traffic must be moved onto the
// canonical `pay*` host. Everything else (pay*/sandbox/internal/localhost/
// custom domains) is left untouched.
const legacyRedirectHosts = new Set(['www', 'staging', 'demo', 'dev']);

// When a payment link is opened on a legacy host (www/staging/demo/dev
// .airwallex.com), returns the canonical short-link URL on the corresponding
// `pay*.airwallex.com` host, e.g.
//   www.airwallex.com/pay/hk/link/1c4c7eb35ac1 -> pay.airwallex.com/hk1c4c7eb35ac1
//   staging.airwallex.com/...                  -> pay-staging.airwallex.com/hk...
// Returns null when no redirect is needed (already on a pay* / sandbox /
// internal host, localhost, or any other/custom host).
export const getPayHostRedirectUrl = (region: string, code: string): string | null => {
  // Guard against a missing host (e.g. tests that stub window.location); an
  // unknown/empty host is not a legacy host, so no redirect is performed.
  // Check the full hostname first: `www.sandbox.airwallex.com` would otherwise
  // match the legacy `www` label and redirect onto prod `pay.airwallex.com`.
  const hostName = window.location.hostname || '';
  if (isSandboxHost(hostName)) {
    return null;
  }
  const env = (window.location.host || '').split('.')[0] as keyof typeof apiEnvMapping;
  if (!legacyRedirectHosts.has(env)) {
    return null;
  }
  // Reuse the shared env suffix: www -> '', staging -> '-staging', etc.
  const targetHost = `pay${apiEnvMapping[env]}.airwallex.com`;
  // Short-link form glues the region into the code; avoid double-prefixing when
  // the code already starts with the region.
  const prefixedCode = code.startsWith(region) ? code : `${region}${code}`;
  const { search = '', hash = '' } = window.location;
  return `https://${targetHost}/${prefixedCode}${search}${hash}`;
};

export const region = window.location.pathname.split('/')[2] || 'hk';

export const getAirwayUrl = (): string => `https://checkout${getApiEnvByHost()}.airwallex.com/api/v1/pa`;

export const getCorpsiteUrl = (): string => {
  const airwallexEnv = getPageEnvByHost();
  if (['staging', 'demo', 'dev'].includes(airwallexEnv)) {
    // Local development
    return `https://${airwallexEnv}.airwallex.com`;
  }
  if (airwallexEnv === 'preview') {
    return 'https://www.sandbox.airwallex.com';
  }
  if (airwallexEnv === 'prod') {
    // Prod deployment
    return 'https://www.airwallex.com';
  }
  // dev deployment
  return 'https://staging.airwallex.com';
};
